import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { Edit2, Paperclip2 } from "iconsax-react";
import { Avatar, Button, Dropdown, DropdownItem, Skeleton, Prompt, PromptForm } from "@/shared/ui";
import { useScrollDetection } from "@/shared/hooks";
import { formatTimeAgo } from "@/shared/utils";
import { useLocale } from "@/shared/hooks";
import type { TaskComment, CommentAttachment } from "../../../types";
import { useTaskComments } from "../../../hooks/useTaskComments";
import {
  ALLOWED_COMMENT_FILE_EXTENSIONS,
  ALLOWED_COMMENT_MIME_TYPES,
  MAX_COMMENT_FILES,
  MAX_COMMENT_FILE_SIZE_MB,
} from "../../../validation";
import { MoreIcon } from "@/shared/assets/icons";
import CommentAttachments from "./CommentAttachments";
import FilePickerPreview from "./FilePickerPreview";
import FileCard from "./FileCard";

interface Props {
  projectId: number;
  taskId: number;
  comments: TaskComment[];
  canComment: boolean;
  isTaskFetching: boolean;
  currentUserId?: number | null;
}

function CommentSkeleton() {
  return (
    <div className="flex gap-3 p-3 radius-md surface-base-secondary">
      <Skeleton circle width={32} height={32} />
      <div className="flex-1">
        <div className="flex items-center justify-between gap-2">
          <Skeleton height={14} width={100} />
          <Skeleton height={12} width={70} />
        </div>
        <div className="mt-1">
          <Skeleton height={12} width="90%" />
        </div>
      </div>
    </div>
  );
}

interface EditFormProps {
  body: string;
  attachments: CommentAttachment[];
  isSubmitting: boolean;
  onSave: (body: string, newFiles?: File[], removeAttachmentIds?: number[]) => void;
  onCancel: () => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}

function CommentEditForm({ body, attachments, isSubmitting, onSave, onCancel, t }: EditFormProps) {
  const [editBody, setEditBody] = useState(body);
  const [keptAttachments, setKeptAttachments] = useState<CommentAttachment[]>(attachments);
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState<number[]>([]);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function handleRemoveExisting(attachmentId: number) {
    setKeptAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
    setRemovedAttachmentIds((prev) => [...prev, attachmentId]);
  }

  function handleAddNewFiles(fileList: FileList) {
    const maxNew = MAX_COMMENT_FILES - keptAttachments.length - newFiles.length;
    if (maxNew <= 0) return;
    const validFiles: File[] = [];
    for (const file of Array.from(fileList)) {
      if (validFiles.length >= maxNew) break;
      if (file.size > MAX_COMMENT_FILE_SIZE_MB * 1024 * 1024) continue;
      if (!ALLOWED_COMMENT_MIME_TYPES.includes(file.type)) continue;
      validFiles.push(file);
    }
    if (validFiles.length > 0) {
      setNewFiles((prev) => [...prev, ...validFiles]);
    }
  }

  function handleRemoveNewFile(index: number) {
    setNewFiles((prev) => prev.filter((_, i) => i !== index));
  }

  function handleSave() {
    const hasBodyChange = editBody !== body;
    const hasFileChanges = removedAttachmentIds.length > 0 || newFiles.length > 0;
    const hasContent = editBody.trim().length > 0 || keptAttachments.length + newFiles.length > 0;

    if (!hasContent) return;
    if (!hasBodyChange && !hasFileChanges) {
      onCancel();
      return;
    }
    onSave(
      editBody,
      newFiles.length > 0 ? newFiles : undefined,
      removedAttachmentIds.length > 0 ? removedAttachmentIds : undefined,
    );
  }

  const totalAttachments = keptAttachments.length + newFiles.length;
  const canAddMore = totalAttachments < MAX_COMMENT_FILES;
  const saveDisabled = isSubmitting || (!editBody.trim() && totalAttachments === 0);

  return (
    <div className="mt-1">
      <textarea
        value={editBody}
        onChange={(e) => setEditBody(e.target.value)}
        disabled={isSubmitting}
        rows={2}
        className="w-full p-2 radius-xs border surface-base-stroke surface-base-fill text-body-regular-sm content-base-primary resize-none focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:opacity-50"
      />

      {keptAttachments.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-1.5">
          {keptAttachments.map((att) => (
            <FileCard
              key={att.id}
              name={att.file_name}
              size={att.file_size}
              contentType={att.content_type}
              onRemove={() => handleRemoveExisting(att.id)}
              disabled={isSubmitting}
            />
          ))}
        </div>
      )}

      {newFiles.length > 0 && (
        <div className="mt-1.5">
          <FilePickerPreview files={newFiles} onRemove={handleRemoveNewFile} disabled={isSubmitting} />
        </div>
      )}

      <div className="flex gap-2 mt-1.5 justify-end items-center">
        {canAddMore && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ALLOWED_COMMENT_FILE_EXTENSIONS}
              className="hidden"
              onChange={(e) => {
                if (e.target.files && e.target.files.length > 0) {
                  handleAddNewFiles(e.target.files);
                }
                e.target.value = "";
              }}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isSubmitting}
              className="w-7 h-7 radius-xs flex items-center justify-center content-base-secondary hover:content-base-primary cursor-pointer disabled:opacity-50">
              <Paperclip2 size={16} color="currentColor" />
            </button>
          </>
        )}
        <Button type="button" variant="secondary" size="sm" onClick={onCancel} disabled={isSubmitting}>
          {t("confirm.cancel")}
        </Button>
        <Button type="button" variant="primary" size="sm" onClick={handleSave} disabled={saveDisabled}>
          {isSubmitting ? t("commentForm.sending") : t("commentForm.save")}
        </Button>
      </div>
    </div>
  );
}

export default function TaskComments({
  projectId,
  taskId,
  comments,
  canComment,
  isTaskFetching,
  currentUserId,
}: Props) {
  const { t } = useTranslation("TasksPage");
  const locale = useLocale();
  const {
    register,
    handleSubmit,
    errors,
    watchedBody,
    isAddingComment,
    deletingId,
    editingId,
    isEditSubmitting,
    confirmDeleteId,
    prompt,
    setPrompt,
    pendingFiles,
    addPendingFiles,
    removePendingFile,
    handleAddComment,
    startEditComment,
    cancelEditComment,
    submitEditComment,
    requestDeleteComment,
    confirmDeleteComment,
    cancelDeleteComment,
  } = useTaskComments({ projectId, taskId, isTaskFetching });
  const [openDropdownId, setOpenDropdownId] = useState<number | null>(null);
  const { scrollRef, hasScroll } = useScrollDetection();
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="h-full min-h-0 p-1">
      <div ref={scrollRef} className={cn("h-full overflow-auto page-scroll", hasScroll && "pr-3")}>
        {comments.length > 0 ? (
          <div className="flex flex-col">
            {comments.map((comment) => {
              const isAuthor = currentUserId != null && comment.author === currentUserId;
              const isEditing = editingId === comment.id;
              const hasBody = comment.body.trim().length > 0;
              const hasAttachments = comment.attachments && comment.attachments.length > 0;

              return (
                <div
                  key={comment.id}
                  className={cn("flex gap-3 p-1.5", deletingId === comment.id && "opacity-50 pointer-events-none")}>
                  <div className="shrink-0 flex flex-col items-center gap-1">
                    <Avatar name={comment.author_name} textClassName="text-body-bold-xs" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="text-label-sm content-base-primary shrink-0 whitespace-nowrap">
                          {comment.author_name}
                        </span>
                        <p className="truncate min-w-0 text-body-regular-xs content-base-secondary">
                          {t("taskDetail.commentedTimeAgo", { timeAgo: formatTimeAgo(comment.created_at, locale) })}
                          {comment.updated_at && comment.updated_at !== comment.created_at && (
                            <span className="ml-0.5 text-body-regular-xs content-base-secondary italic">
                              ({t("taskDetail.edited")})
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {isAuthor && canComment && (
                          <button
                            type="button"
                            onClick={() => (isEditing ? cancelEditComment() : startEditComment(comment.id))}
                            className={cn(
                              "w-5 h-5 radius-2xs flex items-center justify-center surface-component-fill hover:content-base-primary cursor-pointer outline-none",
                              isEditing ? "content-base-primary" : "content-base-secondary",
                            )}
                            disabled={deletingId !== null}>
                            <Edit2 size={12} color="currentColor" />
                          </button>
                        )}
                        {canComment && (
                          <Dropdown
                            open={openDropdownId === comment.id}
                            onClose={() => setOpenDropdownId(null)}
                            align="right"
                            className="-mt-1">
                            <button
                              type="button"
                              onClick={() => setOpenDropdownId((prev) => (prev === comment.id ? null : comment.id))}
                              className="w-5 h-5 radius-2xs flex items-center justify-center surface-component-fill content-base-secondary hover:content-base-primary cursor-pointer outline-none"
                              disabled={deletingId !== null}>
                              <MoreIcon size={12} />
                            </button>
                            <DropdownItem
                              onClick={() => {
                                setOpenDropdownId(null);
                                requestDeleteComment(comment.id);
                              }}>
                              {t("confirm.delete")}
                            </DropdownItem>
                          </Dropdown>
                        )}
                      </div>
                    </div>
                    {isEditing ? (
                      <CommentEditForm
                        body={comment.body}
                        attachments={comment.attachments || []}
                        isSubmitting={isEditSubmitting}
                        onSave={(body, newFiles, removeIds) => submitEditComment(comment.id, body, newFiles, removeIds)}
                        onCancel={cancelEditComment}
                        t={t}
                      />
                    ) : (
                      <>
                        {hasBody && (
                          <div className="mt-2 px-5 py-3 surface-component-fill rounded-[2px_16px_16px_16px]">
                            <p className="text-body-regular-sm content-base-primary whitespace-pre-wrap">
                              {comment.body}
                            </p>
                          </div>
                        )}
                        {hasAttachments && <CommentAttachments attachments={comment.attachments} />}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          !isAddingComment && (
            <p className="text-body-regular-sm content-base-secondary">{t("taskDetail.noComments")}</p>
          )
        )}

        {isAddingComment && <CommentSkeleton />}

        {canComment && (
          <form
            onSubmit={handleSubmit(handleAddComment)}
            className="flex flex-col gap-2 sticky bottom-0 surface-base-fill p-1 mt-3">
            <div className="relative">
              <textarea
                {...register("body")}
                placeholder={t("commentForm.placeholder")}
                rows={2}
                disabled={isAddingComment}
                className="w-full p-3 pr-10 radius-xs border surface-base-stroke surface-base-fill text-body-regular-sm content-base-primary resize-none focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:opacity-50"
              />
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={ALLOWED_COMMENT_FILE_EXTENSIONS}
                className="hidden"
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) {
                    addPendingFiles(Array.from(e.target.files));
                  }
                  e.target.value = "";
                }}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isAddingComment || pendingFiles.length >= MAX_COMMENT_FILES}
                className="absolute top-0 right-0 w-10 h-10 radius-xs flex items-center justify-center content-base-secondary hover:content-base-primary cursor-pointer disabled:opacity-50">
                <Paperclip2 size={16} color="currentColor" />
              </button>
            </div>
            {errors.body?.message && (
              <p className="text-body-regular-xs content-action-negative mt-1">{t(errors.body.message)}</p>
            )}

            {!isAddingComment && (
              <FilePickerPreview files={pendingFiles} onRemove={removePendingFile} disabled={false} />
            )}

            <div className="flex justify-end">
              <Button
                type="submit"
                variant="primary"
                size="md"
                disabled={isAddingComment || (!watchedBody.trim() && pendingFiles.length === 0)}>
                {isAddingComment ? t("commentForm.sending") : t("commentForm.send")}
              </Button>
            </div>
          </form>
        )}
      </div>

      {confirmDeleteId !== null && (
        <PromptForm
          title={t("confirm.deleteCommentTitle")}
          text={t("confirm.deleteCommentText")}
          variant="error"
          onClose={cancelDeleteComment}
          onConfirm={confirmDeleteComment}
          isLoading={deletingId !== null}
          confirmText={t("confirm.delete")}
          cancelText={t("confirm.cancel")}
          namespace="TasksPage"
        />
      )}

      {prompt && (
        <Prompt
          title={prompt.title}
          text={prompt.text}
          variant={prompt.variant}
          onClose={() => setPrompt(null)}
          namespace="TasksPage"
        />
      )}
    </div>
  );
}
